import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { TURN_STATUS, MAX_ATTACHMENTS } from 'Pods/ThreeDStudio/constants'
import useSourceUrlImageUpload from 'Pods/ThreeDStudio/Hooks/useSourceUrlImageUpload'
import { setInputImage } from 'Pods/ThreeDStudio/Store/actions/modelSettings'
import { closeImageAssistant, setEditImage, setImageAssistantTab } from 'Pods/ThreeDStudio/Store/actions/ui'
import { IMAGE_ASSISTANT_TAB } from 'Pods/ThreeDStudio/Store/constants'

import { EMPTY_ARRAY } from 'Utils/stableDefaults'

import { CREATE_TAGS, mockGenerateImage } from '../../mockData'

/**
 * Owns the Create tab's state: the composer (prompt + reference attachments) and
 * the conversation of generation turns, plus the handlers wiring those to the
 * (mocked) generation backend and the store.
 */
export default function useCreateTab() {
  const [prompt, setPrompt] = useState('')
  const [attachments, setAttachments] = useState(EMPTY_ARRAY)
  const [messages, setMessages] = useState(EMPTY_ARRAY)

  const fileInputRef = useRef(null)
  const idRef = useRef(0)
  const isMountedRef = useRef(true)
  useEffect(() => () => { isMountedRef.current = false }, [])

  const uploadFromUrl = useSourceUrlImageUpload(setInputImage)

  const nextId = () => {
    idRef.current += 1
    return idRef.current
  }

  // Reference-image attachments drive which quick-action tags are offered.
  const tags = useMemo(() => {
    if (attachments.length === 0) {
      return EMPTY_ARRAY
    }
    return attachments.length === 1 ? CREATE_TAGS.SINGLE : CREATE_TAGS.MULTI
  }, [attachments])

  const isGenerating = useMemo(
    () => messages.some(message => message.status === TURN_STATUS.LOADING),
    [messages],
  )

  const runGeneration = useCallback((turnId) => {
    mockGenerateImage().then((outcome) => {
      if (!isMountedRef.current) {
        return
      }
      setMessages(prev => prev.map((message) => {
        if (message.id !== turnId) {
          return message
        }
        return outcome.failed
          ? { ...message, status: TURN_STATUS.FAILED, result: null }
          : { ...message, status: TURN_STATUS.DONE, result: outcome.image }
      }))
    })
  }, [])

  const handlePromptChange = useCallback(event => setPrompt(event.target.value), [])

  const handleTagClick = useCallback(tag => setPrompt(tag), [])

  const handleUploadClick = useCallback(() => fileInputRef.current?.click(), [])

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => {
      const removed = prev.find(image => image.id === id)
      if (removed) {
        URL.revokeObjectURL(removed.url)
      }
      return prev.filter(image => image.id !== id)
    })
  }, [])

  const handleFilesChange = useCallback((event) => {
    const picked = Array.from(event.target.files || [])
    const remaining = MAX_ATTACHMENTS - attachments.length
    if (picked.length && remaining > 0) {
      const added = picked
        .slice(0, remaining)
        .map(file => ({ id: nextId(), url: URL.createObjectURL(file) }))
      setAttachments(prev => [...prev, ...added])
    }
    // Reset so picking the same file again still fires change.
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [attachments])

  const handleSend = useCallback(() => {
    const trimmed = prompt.trim()
    if (!trimmed && attachments.length === 0) {
      return
    }

    const turnId = nextId()
    setMessages(prev => [
      ...prev,
      { id: turnId, prompt: trimmed, images: attachments, status: TURN_STATUS.LOADING, result: null },
    ])
    setPrompt('')
    setAttachments(EMPTY_ARRAY)
    runGeneration(turnId)
  }, [prompt, attachments, runGeneration])

  // Regenerate re-sends the same request and appends only the new image — the
  // sent prompt/images are not shown again (continuation turn).
  const handleRegenerate = useCallback((turn) => {
    const turnId = nextId()
    setMessages(prev => [
      ...prev,
      {
        id: turnId,
        prompt: turn.prompt,
        images: turn.images,
        status: TURN_STATUS.LOADING,
        result: null,
        continuation: true,
      },
    ])
    runGeneration(turnId)
  }, [runGeneration])

  // Retry a failed turn in place: flip it back to loading and re-run.
  const handleGenerateAgain = useCallback((turn) => {
    setMessages(prev => prev.map(message => (
      message.id === turn.id ? { ...message, status: TURN_STATUS.LOADING, result: null } : message
    )))
    runGeneration(turn.id)
  }, [runGeneration])

  const handleApply = useCallback(async (image) => {
    const absoluteUrl = new URL(image.url, window.location.origin).href
    const uploaded = await uploadFromUrl(absoluteUrl)
    if (uploaded) {
      closeImageAssistant()
    }
  }, [uploadFromUrl])

  const handleEdit = useCallback((image) => {
    setEditImage(image)
    setImageAssistantTab(IMAGE_ASSISTANT_TAB.EDIT)
  }, [])

  return {
    prompt,
    attachments,
    messages,
    tags,
    isGenerating,
    isUploadDisabled: attachments.length >= MAX_ATTACHMENTS,
    fileInputRef,
    handlePromptChange,
    handleTagClick,
    handleUploadClick,
    handleRemoveAttachment,
    handleFilesChange,
    handleSend,
    handleRegenerate,
    handleGenerateAgain,
    handleApply,
    handleEdit,
  }
}
